import { useTranslation } from 'react-i18next';
import { KoobButton, KoobId, KoobInputNumber, KoobTableAction } from '@koob/ui';
import React, { useEffect, useState } from 'react';
import Header from './Partials/Header';
import RequestLoadError from './Partials/RequestLoadError';
import { get } from '../../../api/node';
import { useApp, useError, useSnack } from '../../../hooks';
import { useAsync } from 'react-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import DistributionBrowser from './Partials/DistributionBrowser';
import DistributionSelected from './Partials/DistributionSelected';
import ProductStateBadge from './Partials/ProductStateBadge';
import { Spinner } from '@koob/margaret';
import qs from 'qs';
import {
  getToConnectionRequestById,
  updateToConnectionRequest,
} from '../../../api/node/toConnectionRequest';
import { PER_PAGE_FOR_LIST } from 'constants';
import ToConnectionRequestUpdating from './Partials/ToConnectionRequestUpdating';
import { decodeId } from 'utils';
import { useDistributionGuard } from '../useDistributionGuard';
import DistributionIncoherenceModal from '../Partials/DistributionIncoherenceModal';

const getList = async ({ filters }) => {
  return await get(`/trip-templates?${qs.stringify(filters)}`);
};

const ToConnectionRequestTemplate = () => {
  const { t } = useTranslation('toConnectionRequest');
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();
  const { requestId } = useParams();
  const {
    isAllowedTo,
    canManageToConnectionRequest,
    user,
    selectedDistributionOrganization,
    currentOrganization,
  } = useApp();
  const navigate = useNavigate();

  const isAllowedToManage = () => {
    return (
      isAllowedTo('canManageToConnectionRequest') ||
      canManageToConnectionRequest(user)
    );
  };
  const dmcId =
    selectedDistributionOrganization?.value ?? currentOrganization?.id;

  const {
    data,
    isLoading,
    error: loadError,
    reload,
  } = useAsync({
    promiseFn: getToConnectionRequestById,
    requestId: requestId,
    dmcId: decodeId(dmcId),
  });
  const request = data?.data;
  // A template whose trip is gone cannot be browsed or opened: it stays out of
  // the page, and saving stops distributing it.
  const distributedTemplates = request?.requestTemplate?.filter(
    (requestTemplate) => requestTemplate.tripTemplate?.trips?.length > 0,
  );

  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const limitPerPage = PER_PAGE_FOR_LIST;

  const formatTemplateData = (listTemplate) => {
    return (listTemplate || []).map((template) => {
      if (template?.tripTemplate) {
        return {
          id: template?.tripTemplate?.id,
          name: template?.tripTemplate?.name,
          state: template?.tripTemplate?.state,
          tripDuration: template.tripTemplate?.trips?.[0]?.duration,
          margin: template.margin,
          isMarginPercent: template.isMarginPercent,
        };
      } else {
        return {
          id: template?.id,
          name: template?.name,
          state: template?.state,
          tripDuration: template?.trips?.[0]?.duration,
          margin: null,
          isMarginPercent: null,
        };
      }
    });
  };

  useEffect(() => {
    if (request) {
      setGlobalMargin(request.margin ?? 0);
      setIsGlobalMarginPercent(request.isMarginPercent ?? false);
    }
    if (distributedTemplates) {
      let formattedTemplate = formatTemplateData(distributedTemplates);

      setSelectedTemplates(formattedTemplate);
    }
  }, [request]);

  const dmcIdDecoded = decodeId(selectedDistributionOrganization?.value);
  const requestFilters = {
    // Typed text wins; a product picked in the location box searches its name.
    search:
      filters?.search ||
      (filters?.location?.kind === 'template' ? filters?.location?.title : ''),
    // KD-3547 — no state filter: a DMC distributes templates it is still
    // preparing (in progress / stand by), not only the available ones.
    page: page,
    perPage: limitPerPage,
    ...(dmcIdDecoded && { dmcId: dmcIdDecoded }),
  };

  const {
    data: templatesList,
    reload: reloadTemplates,
    isLoading: templatesLoading,
  } = useAsync({
    promiseFn: getList,
    filters: requestFilters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });

  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState(
    request?.templates || [],
  );
  const [globalMargin, setGlobalMargin] = useState(0);
  const [isGlobalMarginPercent, setIsGlobalMarginPercent] = useState(false);
  const [saving, setSaving] = useState(false);

  const changedTemplates = selectedTemplates?.filter((template) => {
    const originalTemplate = distributedTemplates?.find(
      (originalTemplate) => originalTemplate.tripTemplate.id === template.id,
    );
    return (
      !originalTemplate ||
      (template.margin !== undefined &&
        originalTemplate?.margin !== template.margin) ||
      (template.isMarginPercent !== undefined &&
        originalTemplate?.isMarginPercent !== template.isMarginPercent)
    );
  });

  const distributionGuard = useDistributionGuard();

  const submitDistribution = async (payload) => {
    setSaving(true);

    try {
      await updateToConnectionRequest({
        toConnectionRequestId: requestId,
        payload,
      });

      notify(t('updated'));
      navigate(-1);
    } catch (error) {
      sendErrorSnack(error);
    } finally {
      setSaving(false);
    }
  };

  const save = async (state, comment) => {
    // The incoherence check is a round trip of its own, so the page has to look
    // busy from here and not only once `submitDistribution` takes over.
    setSaving(true);

    try {
      const payload = {
        margin: globalMargin ? parseFloat(globalMargin) : 0,
        isMarginPercent: isGlobalMarginPercent,
        state: state ?? request.state,
        explaination: comment?.reason,
        toConnectionRequestTemplates: [
          ...changedTemplates.map((template) => ({
            id: template.id,
            margin: template.margin ? parseFloat(template.margin) : null,
            isMarginPercent: template.isMarginPercent,
            distribute: true,
          })),
          ...(request.requestTemplate
            ?.filter(
              (template) =>
                !selectedTemplates.some(
                  (e) => e.id === template.tripTemplate.id,
                ),
            )
            .map((requestTemplate) => ({
              id: requestTemplate.tripTemplate.id,
              distribute: false,
            })) ?? []),
        ],
      };

      // Warns about templates whose products this TO cannot see; distributes
      // straight away when there is nothing to report.
      await distributionGuard.guard({
        toConnectionRequestIds: [requestId],
        request: payload,
        submit: submitDistribution,
      });
    } finally {
      // Also releases the page when the guard stops on the modal, otherwise its
      // own confirm button would stay disabled by `submitting`.
      setSaving(false);
    }
  };

  const setTemplateAttribute = (templateId, attribute, value) => {
    setSelectedTemplates((current) =>
      current.map((template) => {
        if (template.id === templateId) {
          return {
            ...template,
            [attribute]: value,
          };
        }
        return template;
      }),
    );
  };

  const isTemplateSelected = (templateId) => {
    return selectedTemplates.some((e) => e.id === templateId);
  };

  const unselectTemplate = (templateId) => {
    setSelectedTemplates(selectedTemplates.filter((e) => e.id !== templateId));
  };

  const removeSelectedTemplates = (templateIds) => {
    setSelectedTemplates(
      selectedTemplates.filter((e) => !templateIds.includes(e.id)),
    );
    notify(t('removeSelectedSuccess', { count: templateIds.length }));
  };

  const onAddTemplate = (template) => {
    if (!isTemplateSelected(template.id)) {
      template.margin = globalMargin;
      template.isMarginPercent = isGlobalMarginPercent;
      setSelectedTemplates([...selectedTemplates, template]);
    } else {
      unselectTemplate(template.id);
    }
  };

  const selectAllShownTemplates = (templateList) => {
    const templatesNotSelected = templateList.filter(
      (template) => !isTemplateSelected(template.id),
    );
    templatesNotSelected.forEach((template) => {
      template.margin = globalMargin;
      template.isMarginPercent = isGlobalMarginPercent;
    });
    setSelectedTemplates([...selectedTemplates, ...templatesNotSelected]);
  };

  const BrowserHeadings = [
    {
      slug: 'name',
      label: t('experienceName'),
      width: '90px',
      cannotBeReordered: true,
    },
    {
      slug: 'tripDuration',
      label: t('tripDuration'),
      width: '60px',
      cannotBeReordered: true,
    },
    {
      slug: 'state',
      label: t('state'),
      width: '70px',
      cannotBeReordered: true,
    },
    {
      slug: 'selected',
      label: t('enabled'),
      width: '50px',
      cannotBeReordered: true,
    },
  ];

  const selectedDatasHeadings = [
    {
      slug: 'name',
      label: t('name'),
      width: '80px',
      cannotBeReordered: true,
    },
    {
      slug: 'tripDuration',
      label: t('tripDuration'),
      width: '60px',
      cannotBeReordered: true,
    },
    {
      slug: 'state',
      label: t('state'),
      width: '70px',
      cannotBeReordered: true,
    },
    {
      slug: 'margin',
      label: t('margin'),
      width: '40px',
      cannotBeReordered: true,
    },
    {
      slug: 'isMarginPercent',
      label: t('percent'),
      width: '60px',
      cannotBeReordered: true,
    },
    {
      slug: 'selected',
      label: t('enabled'),
      width: '40px',
      cannotBeReordered: true,
    },
  ];

  const templateSelectedDataTable = (selectedTemplates || []).map((node) => ({
    id: node?.id,
    name: {
      render: () => (
        <div className="flex flex-col items-start">
          <span className="font-medium">{node?.name}</span>
          <KoobId id={node?.id} />
        </div>
      ),
    },
    tripDuration: {
      value: node?.tripDuration,
    },
    state: {
      render: () => <ProductStateBadge state={node?.state} />,
    },
    margin: {
      render: () => (
        <KoobInputNumber
          min={0}
          value={node?.margin}
          onValueChange={(value) =>
            setTemplateAttribute(node.id, 'margin', value ?? '')
          }
          disabled={!isAllowedToManage()}
        />
      ),
    },
    isMarginPercent: {
      render: () => (
        <input
          type="checkbox"
          checked={node?.isMarginPercent}
          onChange={() =>
            setTemplateAttribute(
              node.id,
              'isMarginPercent',
              !node.isMarginPercent,
            )
          }
          disabled={!isAllowedToManage()}
        />
      ),
    },
    selected: {
      render: () =>
        isAllowedToManage() ? (
          <KoobTableAction
            icon="delete"
            variant="danger"
            label={t('misc:delete')}
            onClick={() => unselectTemplate(node.id)}
          />
        ) : null,
    },
  }));

  const tableTemplates = (templates || []).map((node) => ({
    id: node?.id,
    name: {
      render: () => (
        <div className="flex flex-col items-start">
          <span className="font-medium">{node?.name}</span>
          <KoobId id={node?.id} />
        </div>
      ),
    },
    tripDuration: {
      value: node?.tripDuration,
    },
    state: {
      render: () => <ProductStateBadge state={node?.state} />,
    },
    selected: {
      render: isAllowedToManage()
        ? () => (
            <KoobTableAction
              icon={
                <i
                  className={`fa-regular fa-eye ${
                    isTemplateSelected(node?.id) ? 'text-orange-500' : ''
                  }`}
                />
              }
              label={t('enabled')}
              onClick={() => {
                onAddTemplate(node);
              }}
            />
          )
        : null,
    },
  }));

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : loadError ? (
        <RequestLoadError onRetry={reload} />
      ) : (
        <div className="px-4">
          <Header
            request={request}
            save={save}
            isLoading={saving}
            isAllowedToManage={isAllowedToManage()}
            selectedDistributionOrganization={selectedDistributionOrganization}
          />

          {!request?.activeJob ? (
            <>
              {isAllowedToManage() && (
                <div className="my-5 flex justify-end gap-5">
                  <KoobButton type="button" variant="secondary" asChild>
                    <Link to={-1}>{t('misc:cancel')}</Link>
                  </KoobButton>
                  <KoobButton
                    onClick={() => save()}
                    variant="primary"
                    disabled={saving}
                  >
                    {t('misc:save')}
                  </KoobButton>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <DistributionSelected
                  selectedDatasHeadings={selectedDatasHeadings}
                  selectedDatas={templateSelectedDataTable}
                  tabTitle={'selectedTemplate'}
                  onRemoveSelected={
                    isAllowedToManage() ? removeSelectedTemplates : undefined
                  }
                />

                {isAllowedToManage() && (
                  <DistributionBrowser
                    browserDatas={templates}
                    setBrowerDatas={setTemplates}
                    selectAllShownItems={selectAllShownTemplates}
                    dataTableDatas={tableTemplates}
                    BrowserHeadings={BrowserHeadings}
                    browserDatasList={templatesList}
                    isLoading={templatesLoading}
                    globalMargin={globalMargin}
                    setGlobalMargin={setGlobalMargin}
                    isGlobalMarginPercent={isGlobalMarginPercent}
                    setIsGlobalMarginPercent={setIsGlobalMarginPercent}
                    formatBrowerData={formatTemplateData}
                    setFilters={setFilters}
                    setPage={setPage}
                    filters={filters}
                    limitPerPage={PER_PAGE_FOR_LIST}
                    reload={reloadTemplates}
                    tabTitle={'browseTemplate'}
                    kind={'template'}
                    searcTranslate={'toConnectionRequest'}
                  />
                )}
              </div>
            </>
          ) : (
            <ToConnectionRequestUpdating />
          )}
        </div>
      )}

      <DistributionIncoherenceModal
        open={distributionGuard.isOpen}
        incoherences={distributionGuard.incoherences}
        submitting={saving}
        onCancel={distributionGuard.cancel}
        onConfirm={distributionGuard.confirm}
      />
    </>
  );
};

export default ToConnectionRequestTemplate;
